import {
  BUDGET_ATTRIBUTION_BUDGET_SCOPE_MISMATCH_CODE,
  BUDGET_ATTRIBUTION_DESTINATION_MISSING_CODE,
  BUDGET_CONCURRENCY_RETRY_CODE,
} from '../budget.js';
import type { ApBillStatus, PaymentMethodType } from '../generated/enums.js';

/** WorkOS org API key scopes for the External API bill surface. */
export const ExternalBillScope = {
  READ: 'external:bills:read',
  WRITE: 'external:bills:write',
} as const;
export type ExternalBillScope = (typeof ExternalBillScope)[keyof typeof ExternalBillScope];

/** All External API scopes (WorkOS org API key allowlist + route guards). */
export const EXTERNAL_API_BILL_SCOPES = [
  ExternalBillScope.READ,
  ExternalBillScope.WRITE,
] as const satisfies readonly ExternalBillScope[];

/** Document ingestion lifecycle exposed on External API bill responses. */
export const ExternalBillProcessingStatus = {
  NONE: 'NONE',
  QUEUED: 'QUEUED',
  PROCESSING: 'PROCESSING',
  AWAITING_PASSWORD: 'AWAITING_PASSWORD',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const;
export type ExternalBillProcessingStatus =
  (typeof ExternalBillProcessingStatus)[keyof typeof ExternalBillProcessingStatus];

/** Stable error codes returned by the External API error envelope. */
export const ExternalApiErrorCode = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  FORBIDDEN: 'FORBIDDEN',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  /** Unexpected throw; never used for invalid caller input. */
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  /** Inbound request rate exceeded the per-API-key/tenant throttling limit. */
  RATE_LIMITED: 'RATE_LIMITED',
  /** Persisted data is unusable for a truthful aggregate or read. */
  DATA_INTEGRITY_ERROR: 'DATA_INTEGRITY_ERROR',
  INVALID_VENDOR: 'INVALID_VENDOR',
  ENTITY_NOT_FOUND: 'ENTITY_NOT_FOUND',
  BILL_NOT_FOUND: 'BILL_NOT_FOUND',
  RECEIVED_NF_NOT_FOUND: 'RECEIVED_NF_NOT_FOUND',
  VENDOR_NOT_FOUND: 'VENDOR_NOT_FOUND',
  INVOICE_NOT_FOUND: 'INVOICE_NOT_FOUND',
  CUSTOMER_NOT_FOUND: 'CUSTOMER_NOT_FOUND',
  /**
   * The party already has an ACTIVE customer in this entity (ALC-4673). Creating a second one
   * would split one customer's receivables across two ledgers, so the create is refused. The
   * External API answer carries only this code and a message (no customer id); the MCP error
   * and the internal API body also name the existing customer. A blocked customer still counts,
   * a deleted or inactive one does not. HTTP 409.
   */
  CUSTOMER_ALREADY_EXISTS: 'CUSTOMER_ALREADY_EXISTS',
  INVALID_CUSTOMER: 'INVALID_CUSTOMER',
  /** No billing contract matches the supplied external reference (ALC-2234). */
  CONTRACT_NOT_FOUND: 'CONTRACT_NOT_FOUND',
  /** A contract already holds this external reference but belongs to a different
   * entity/customer — replaying would silently return someone else's contract. */
  CONTRACT_REFERENCE_CONFLICT: 'CONTRACT_REFERENCE_CONFLICT',
  /** The external reference exceeds `EXTERNAL_CONTRACT_REFERENCE_MAX_LENGTH`. */
  CONTRACT_EXTERNAL_REFERENCE_TOO_LONG: 'CONTRACT_EXTERNAL_REFERENCE_TOO_LONG',
  /** The requested mutation is not legal from the contract's current status. */
  CONTRACT_STATE_CONFLICT: 'CONTRACT_STATE_CONFLICT',
  /**
   * The invoice's currency differs from the currency the named contract is denominated in
   * (ALC-2235, accounting gate ACC-1 / rule ACC-02).
   *
   * Binding a charge in one currency to a subscription stated in another silently mixes
   * units in that contract's revenue: the contract's billed total stops being expressible
   * as a single amount, and any comparison of contracted versus billed becomes arithmetic
   * on two different things. The mismatch is refused rather than converted, because the
   * External API is not told a rate and inventing one would post revenue at a number
   * nobody agreed to.
   */
  CONTRACT_CURRENCY_MISMATCH: 'CONTRACT_CURRENCY_MISMATCH',
  /**
   * `billingPeriodStart` does not identify a period the named contract actually bills
   * (ALC-2801, accounting gate CFO-1 / rule CFO-02) — it falls outside the contract's
   * active window, or is not a valid date.
   *
   * The marker declares "this invoice IS the full recurring charge for that cycle", which
   * SUPPRESSES the forecast's projection for it. A marker pointing at a period the
   * contract does not bill would delete projected cash that nothing replaces.
   */
  CONTRACT_BILLING_PERIOD_INVALID: 'CONTRACT_BILLING_PERIOD_INVALID',
  /** No billing service matches the supplied `serviceId`/`serviceCode`. */
  SERVICE_NOT_FOUND: 'SERVICE_NOT_FOUND',
  /**
   * The requested mutation is not legal from the invoice's current status
   * (ALC-2235) — e.g. marking a VOID invoice paid, or cancelling an invoice a
   * real receipt has already been allocated against. Distinct from
   * `VALIDATION_ERROR`: the payload is well-formed, the invoice is simply not in
   * a state that permits the transition, so a retry after the state changes can
   * succeed.
   */
  INVOICE_STATE_CONFLICT: 'INVOICE_STATE_CONFLICT',
  /**
   * Cancellation was refused because the invoice carries an AUTHORIZED NFS-e
   * emission (ALC-2235). Voiding it here would reverse the GL while leaving a
   * live fiscal document outstanding. The NFS-e cancellation flow — which files
   * the cancellation with the municipality and reverses in step with it — owns
   * that case. On the External API, send `cancelFiscalNote: true` with the scope
   * `external:invoices:cancel-fiscal` to run it.
   */
  INVOICE_FISCAL_NOTE_ACTIVE: 'INVOICE_FISCAL_NOTE_ACTIVE',
  /**
   * `cancelFiscalNote` was sent, but the invoice's NFS-e is still being emitted (PENDING or
   * PROCESSING). It can be cancelled only after it is authorized. Retryable. HTTP 409.
   */
  INVOICE_FISCAL_NOTE_IN_FLIGHT: 'INVOICE_FISCAL_NOTE_IN_FLIGHT',
  /**
   * The NFS-e cancellation was refused before it was filed: its GL reversal does not
   * validate (for example, no open period on the reversal date). The message lists the
   * errors. Nothing was written. HTTP 409.
   */
  FISCAL_NOTE_CANCELLATION_INVALID: 'FISCAL_NOTE_CANCELLATION_INVALID',
  /**
   * `cancelFiscalNote` was sent, but the invoice's NFS-e is already being cancelled for
   * re-issue (the AR screen's cancel that returns the invoice to DRAFT), not to void it.
   * Reusing that cancellation would leave the invoice DRAFT with its installments active.
   * Nothing was written. Send the request again once that cancellation finishes. HTTP 409.
   */
  FISCAL_NOTE_CANCELLATION_FOR_REISSUE: 'FISCAL_NOTE_CANCELLATION_FOR_REISSUE',
  /**
   * WARNING code, never an error (create with `autoApprove: true`). The invoice was created,
   * but its approval was refused, so it stays DRAFT or BLOCKED (the status the approval
   * left). The message names the internal reason
   * code. Send the same create again, after the cause is fixed, to retry the approval.
   */
  AUTO_APPROVE_FAILED: 'AUTO_APPROVE_FAILED',
  /**
   * WARNING code, never an error (ALC-5184). The invoice was recorded as a DRAFT with an open
   * issue that stops a later step (for example a customer CPF that is not valid, so the NFS-e
   * cannot be issued). One warning per open issue: `field` is the field to fix and the message
   * names the issue code; `issues[]` on the response lists them. Send the same
   * `externalReferenceId` again with the fixed data while the invoice is DRAFT to update it.
   */
  INVOICE_HAS_OPEN_ISSUES: 'INVOICE_HAS_OPEN_ISSUES',
  /**
   * WARNING code, never an error (ALC-5184): `autoApprove: true` (or a PAID intent) on an invoice
   * with open issues. The invoice stays DRAFT; a PAID intent still claims its payment
   * reference. Resend the fixed data and the approval runs then.
   */
  AUTO_APPROVE_SKIPPED_OPEN_ISSUES: 'AUTO_APPROVE_SKIPPED_OPEN_ISSUES',
  /**
   * WARNING code, never an error (create with `paymentStatus: PAID` and `autoApprove: true`).
   * The invoice was approved and waits for its NFS-e (AWAITING_NF), so the payment was NOT
   * recorded. Send the payment again (the same create, or mark-paid) once the invoice is
   * ISSUED. Without `autoApprove`, the same case is the error `INVOICE_AWAITING_FISCAL_EMISSION`.
   */
  PAYMENT_PENDING_FISCAL_EMISSION: 'PAYMENT_PENDING_FISCAL_EMISSION',
  /**
   * A payment could not be recorded because the invoice is awaiting asynchronous NFS-e
   * emission (ALC-2235). Recording it from that status would strand the claim: the
   * reconciliation matcher does not source AWAITING_NF invoices and the receipt flow
   * refuses to allocate against them, so the bank credit would never be matched.
   * Retryable — call mark-paid again once the invoice reaches ISSUED.
   */
  INVOICE_AWAITING_FISCAL_EMISSION: 'INVOICE_AWAITING_FISCAL_EMISSION',
  /**
   * Two or more customer identifiers in one request resolve to DIFFERENT customers
   * (e.g. `customerId` of A with `customerTaxId` of B). Binding a contract — and its AR,
   * billing and revenue — to whichever identifier happened to be checked first would be
   * silent and unrecoverable, so the ambiguous payload is refused.
   */
  CUSTOMER_IDENTIFIERS_CONFLICT: 'CUSTOMER_IDENTIFIERS_CONFLICT',
  /**
   * One contract item supplied both `serviceId` and `serviceCode` and they name
   * DIFFERENT services. The chosen service's billing policy, classification and revenue
   * account are frozen into the contract Version, so picking whichever identifier was
   * checked first would silently bill and recognize against the wrong service.
   */
  SERVICE_IDENTIFIERS_CONFLICT: 'SERVICE_IDENTIFIERS_CONFLICT',
  /**
   * A price change omitted a service the current Version still carries. Dropping a
   * service is out of scope for v1 (repo rule: wholesale-replace endpoints must prove
   * non-loss), so the incomplete list is refused rather than silently retiring revenue.
   */
  CONTRACT_ITEMS_INCOMPLETE: 'CONTRACT_ITEMS_INCOMPLETE',
  /**
   * The request would change an existing customer's tax id. Tax-id
   * corrections are in-app only (v1 narrowing, ALC-2233) — the External API
   * matches customers by tax id, so changing it here would silently
   * re-point the customer at a different `Party`.
   */
  CUSTOMER_TAX_ID_MISMATCH: 'CUSTOMER_TAX_ID_MISMATCH',
  /**
   * `POST /v1/customers` only: a customer in Brazil sent with a `taxIdValue` that is not a valid
   * CPF or CNPJ (wrong length or check digits). Nothing is written. A customer abroad is not
   * checked. HTTP 400. The invoice routes never answer it: there the invoice is recorded as a
   * DRAFT with the open issue `CUSTOMER_TAX_ID_INVALID` and the warning `INVOICE_HAS_OPEN_ISSUES`
   * (ALC-5184).
   */
  INVALID_CUSTOMER_TAX_ID: 'INVALID_CUSTOMER_TAX_ID',
  /**
   * A write raced another request for the same `externalReferenceId` /
   * tax id and could not be resolved to a single existing customer after
   * the fact (e.g. a soft-deleted row still holding the code). Distinct
   * from the normal idempotent-replay path, which never reaches this code.
   */
  CUSTOMER_CODE_CONFLICT: 'CUSTOMER_CODE_CONFLICT',
  /**
   * `metadata` failed a v1 pass-through bound (ALC-2236): not a plain object (arrays
   * and primitives are rejected), a value that is not a string, more than 20 keys, a
   * key longer than 64 characters, or a value longer than 256 characters.
   *
   * Deliberately its own code, not `VALIDATION_ERROR`: `metadata` shape rules are
   * enforced by a service-layer helper rather than a class-validator decorator (see
   * `assertValidMetadata` in `external-metadata.util.ts`), so an integrator can branch
   * on this specific failure instead of parsing the message.
   */
  METADATA_INVALID: 'METADATA_INVALID',
  BUDGET_CONCURRENCY_RETRY: BUDGET_CONCURRENCY_RETRY_CODE,
  BUDGET_ATTRIBUTION_DESTINATION_MISSING: BUDGET_ATTRIBUTION_DESTINATION_MISSING_CODE,
  BUDGET_ATTRIBUTION_BUDGET_SCOPE_MISMATCH: BUDGET_ATTRIBUTION_BUDGET_SCOPE_MISMATCH_CODE,
  /**
   * A `POST /v1/services` or `PUT /v1/services/by-code/:code` body named a field outside
   * `EXTERNAL_SERVICE_WRITABLE_FIELDS` (ALC-2819) — every ISS/IBS/CBS/NFS-e field,
   * `glRevenueAccountId`, `managerialAccountId`, `costCenterId`, `fiscalServiceTypeId`,
   * `defaultRecognitionMethod`, `defaultBillingTiming`, or any other column not on the
   * allow-list. Casa do Saber's system owns charge-type identity (code/name/billing
   * mode/currency/status/externalProductId); fiscal configuration and revenue recognition
   * are set up in-app. Deliberately deny-by-default — a future ERP-owned column added to
   * `BillingService` is rejected automatically, not silently accepted, because it is not
   * on the allow-list rather than because it is on a maintained deny-list.
   */
  SERVICE_FIELD_NOT_WRITABLE: 'SERVICE_FIELD_NOT_WRITABLE',
  /**
   * `POST /v1/services` was called with a `code` that already identifies a service under
   * a DIFFERENT entity (ALC-2819). `BillingService.code` is unique per tenant, not per
   * entity, so replaying a create is only safe when the entity agrees too — otherwise the
   * request would resolve to (and could then mutate, through the update route) a service
   * that belongs to someone else's book.
   */
  SERVICE_REFERENCE_CONFLICT: 'SERVICE_REFERENCE_CONFLICT',
  /**
   * No contract template matches the supplied code (ALC-2820), or the caller corroborated
   * it with an `entityId` the stored template disagrees with.
   *
   * The real keying policy, stated once here because every by-code route on this rail
   * follows it:
   *
   * - Cross-TENANT existence is never disclosed. Every lookup is scoped to the API key's
   *   tenant first, so another tenant's identical code is invisible, not a conflict.
   * - WITHIN a tenant, `BillingContractTemplate.code` is tenant-global
   *   (`@@unique([tenantId, code])`), not per-entity. The by-code routes therefore operate
   *   tenant-wide, and omitting `entityId` is the ordinary way to call them.
   * - `entityId` is CORROBORATION when supplied, never a filter that narrows the search: a
   *   template found under a different entity answers `TEMPLATE_NOT_FOUND`, the same code
   *   an unknown template gets, so a caller who names the wrong book learns nothing about
   *   whether the code exists. Same philosophy as `CONTRACT_NOT_FOUND`.
   * - CREATE is the one operation that refuses instead of corroborating: replaying a code
   *   under a different entity is `TEMPLATE_CODE_CONFLICT` (409), because silently
   *   returning the other entity's template would hand the caller a template they did not
   *   create.
   *
   * `SERVICE_NOT_FOUND`/`SERVICE_REFERENCE_CONFLICT` follow the identical policy on the
   * services rail.
   */
  TEMPLATE_NOT_FOUND: 'TEMPLATE_NOT_FOUND',
  /**
   * The named template is inactive (soft-deleted). Refused only for creating a NEW
   * contract from it — contracts already created from the template before it was
   * inactivated are untouched and keep billing normally.
   */
  TEMPLATE_INACTIVE: 'TEMPLATE_INACTIVE',
  /** A template already holds this `code` but belongs to a different entity — replaying
   * would silently return someone else's template. */
  TEMPLATE_CODE_CONFLICT: 'TEMPLATE_CODE_CONFLICT',
  /**
   * The template create payload named a field outside the External API's writable
   * allowlist (code, name, billingFrequency, defaultDurationMonths, renewal terms,
   * paymentMethod, installmentCount/installmentIntervalDays, items). Recognition/timing
   * defaults (`defaultBillingTiming`, `defaultServicePeriodRule`,
   * `defaultRecognitionMethod`) and similar ERP-owned fields are deny-by-default rather
   * than silently dropped — see `assertOnlyWritableFields`.
   */
  TEMPLATE_FIELD_NOT_WRITABLE: 'TEMPLATE_FIELD_NOT_WRITABLE',
  /**
   * The charge names a contract and a `billingPeriodStart`, but the contract version
   * effective for that period carries MORE THAN ONE item for the invoice's service
   * (ALC-2821). Which performance obligation the charge settles decides which deferral
   * schedule its revenue is released from, and picking whichever item was read first would
   * defer against an obligation the charge did not bill. Refused rather than guessed —
   * mirrors `CONTRACT_EVENT_ITEM_VERSION_AMBIGUOUS` on the internal recognition path.
   */
  CONTRACT_ITEM_AMBIGUOUS: 'CONTRACT_ITEM_AMBIGUOUS',
  /**
   * Another live invoice on the same contract already bills the SAME contract cycle
   * (ALC-2821, accounting gate ACC-1).
   *
   * A deferred obligation is keyed by (contract, version, item, service window), and
   * issuance FUNDS that window's schedule with the amount it deferred. A second charge for
   * the same window would overwrite the first invoice's schedule with only its own amount,
   * leaving the Contract Liability both invoices credited with only one invoice's worth of
   * recognition entries to release it — a balance that can never net to zero.
   *
   * The comparison is by contract OCCURRENCE. `billingPeriodStart` must itself BE an
   * occurrence boundary (a non-boundary date is refused with
   * `CONTRACT_BILLING_PERIOD_INVALID`), so in practice the sent date and the window start are
   * the same value — but the range comparison is kept, because rows written before that rule
   * existed can still carry a mid-cycle date and must still collide.
   *
   * Voided invoices are excluded — reissuing a cancelled charge for the same cycle is
   * supported and reactivates the cancelled schedule.
   */
  CONTRACT_BILLING_PERIOD_ALREADY_BILLED: 'CONTRACT_BILLING_PERIOD_ALREADY_BILLED',
  /**
   * Cancellation was refused because the invoice's deferred revenue has already been
   * recognized for one or more months of its service period (ALC-2821).
   *
   * A deferred charge's revenue reaches the P&L month by month as the service is delivered.
   * Voiding the invoice retires the REMAINING plan, but it cannot un-recognize months that
   * have already been earned and posted — doing that silently would remove revenue from a
   * period that may well be closed. Those recognitions must be reversed explicitly first,
   * which leaves an audit trail; then the cancellation succeeds.
   *
   * Distinct from `INVOICE_STATE_CONFLICT`: the invoice's STATUS permits cancellation, and it
   * is the recognition history behind it that does not.
   */
  INVOICE_REVENUE_ALREADY_RECOGNIZED: 'INVOICE_REVENUE_ALREADY_RECOGNIZED',
  /**
   * Issuance was refused because the invoice names a contract billing period whose obligation
   * is recognized over time, but its lines are not linked to that obligation (ALC-2821).
   *
   * The two halves of a deferral — the header's cycle marker and the per-line contract
   * obligation the GL posting reads — are written together, but an invoice can predate that
   * or have been half-written by an interrupted create. Issuing in that state would recognize
   * the whole charge as revenue on the issue date while the marker says it covers a full
   * service period, and posting is irreversible without a journal reversal. Retryable:
   * re-sending the same charge completes the link.
   */
  INVOICE_DEFERRAL_LINK_INCOMPLETE: 'INVOICE_DEFERRAL_LINK_INCOMPLETE',
  /**
   * The charge names a contract and a `billingPeriodStart`, but the contract version effective
   * for that cycle sells NO item for the charge's service (ALC-2821).
   *
   * A de-para (mapping) error on the integrator's side, not a business fact: the charge claims
   * to be the full recurring charge for a cycle of a contract that does not sell what it is
   * charging for. It used to be indistinguishable from a legitimate point-in-time item — both
   * simply "did not defer" — so the charge posted 100% of its revenue on the issue date while
   * its own marker promised a deferral across the whole service period, silently and
   * irreversibly once posted.
   *
   * Fix the service the charge names, or send it without `billingPeriodStart` if it is not the
   * full cycle charge. Distinct from `CONTRACT_ITEM_AMBIGUOUS`, which is the opposite problem
   * (several items match).
   */
  CONTRACT_SERVICE_NOT_ON_CONTRACT: 'CONTRACT_SERVICE_NOT_ON_CONTRACT',
  /**
   * The contract is being amended or regenerated right now, so the operation could not take
   * the lock it needs and refused instead of queueing (ALC-2821).
   *
   * RETRYABLE and transient — the only code in this vocabulary that is. The revenue engine
   * deliberately refuses to wait for the contract lock (it would close a deadlock cycle
   * against journal posting), so a genuinely concurrent amendment surfaces here rather than
   * blocking. Retry the identical request once the amendment settles; the create route is
   * idempotent on `externalReferenceId`, so a retry cannot double-charge.
   */
  CONTRACT_BUSY: 'CONTRACT_BUSY',
  /**
   * The service window the charge bills is covered by a DIFFERENT contract version than the
   * one it is being billed under — an amendment took effect inside or before that window
   * (ALC-2821).
   *
   * A window split across two versions cannot be billed or recognized as one: part of it was
   * sold at the old price and part at the new, and a single schedule would recognize the whole
   * window at whichever price it happened to read. Re-send the charge for the cycle under the
   * current version, or bill the two part-windows separately.
   */
  CONTRACT_VERSION_SUPERSEDED: 'CONTRACT_VERSION_SUPERSEDED',
  /**
   * Deferring this charge requires the tenant's Contract Liability (or Contract Asset) system
   * account slot to be mapped on the CoA version the invoice posts under, and it is not
   * (ALC-2821).
   *
   * A TENANT CONFIGURATION gap, not a payload problem — no change to the request will clear
   * it. The ERP administrator maps the `AR_CONTRACT_LIABILITY` / `AR_CONTRACT_ASSET` slot, and
   * the same charge then succeeds on retry.
   */
  CONTRACT_REVENUE_ACCOUNT_UNMAPPED: 'CONTRACT_REVENUE_ACCOUNT_UNMAPPED',
  /**
   * A contract price change was refused because it would retire a performance obligation an
   * already-issued invoice funded (ALC-2821).
   *
   * The invoice credited Contract Liability for that service window and its recognition
   * entries are what release it. Amending over the window would retire the plan while the
   * liability stands, stranding a balance nothing can clear. Reverse or credit-note the
   * funding invoice first, then re-send the price change.
   */
  CONTRACT_AMENDMENT_OVER_FUNDED_OBLIGATION: 'CONTRACT_AMENDMENT_OVER_FUNDED_OBLIGATION',
  /**
   * `POST /v1/contracts` was called with a PAST `startDate` and did not carry the elapsed
   * cycles' invoices inline (ALC-2823, accounting gate ACC-1 / CFO-1). HTTP 422.
   *
   * The External API does NOT auto-generate the charges a backdated subscription missed.
   * It cannot: only the integrator knows what was actually invoiced, in what amounts, under
   * which fiscal document and whether it was paid — and a generated charge would either
   * duplicate a real NFS-e or invent a receivable that no document backs. Before this code
   * existed the route simply activated with `automationStartDate` at the current occurrence
   * boundary, so the in-force cycle was billed by the recurring engine while the elapsed
   * ones were silently dropped, with only a response warning to say so.
   *
   * The rule is therefore: a retroactive create must SUPPLY the history. Every occurrence
   * already due at the moment of the call (`collectDueContractBillingOccurrences` from
   * `startDate` to today) must be named by exactly one `invoices[]` entry carrying that
   * occurrence's `billingPeriodStart`. The same code is returned when `invoices[]` is
   * present but leaves a due occurrence uncovered — the failure is identical from the
   * caller's side ("the history this contract needs is not in the payload") and splitting
   * it into two codes would make an integrator branch on a distinction that changes nothing
   * about the fix.
   *
   * 422 rather than 400 on purpose: the payload is syntactically valid and every field in
   * it is well-formed. What cannot be processed is the REQUEST — creating this contract
   * requires facts the caller did not send. The only code on this rail that is not
   * 400/404/409.
   */
  RETROACTIVE_REQUIRES_INVOICES: 'RETROACTIVE_REQUIRES_INVOICES',
  /**
   * The `installments` block on a charge is malformed (ALC-2824) — the SHAPE is wrong, as
   * opposed to the arithmetic (`INSTALLMENT_TOTAL_MISMATCH` below).
   *
   * Covers: supplying neither the generated form (`count` + `firstDueDate` +
   * `intervalDays`) nor the explicit form (`rows`); supplying BOTH; a `count` outside
   * 2..60 or an `intervalDays` outside 1..366 (the same bounds the contract-template rail
   * enforces on `installmentCount`/`installmentIntervalDays`); `rows` empty, longer than
   * 60, carrying `installmentNumber`s that are not exactly 1..N with no gaps or repeats,
   * or an unparseable date or non-positive amount on any row.
   *
   * Its own code rather than `VALIDATION_ERROR` because the block is validated in the
   * service layer against the invoice's resolved collectible total, not by a
   * class-validator decorator — an integrator can branch on it instead of parsing prose.
   */
  INSTALLMENT_SPEC_INVALID: 'INSTALLMENT_SPEC_INVALID',
  /**
   * Explicit `installments.rows` do not sum EXACTLY to the invoice's collectible total
   * (ALC-2824). Refused, never absorbed by adjusting a row.
   *
   * The collectible total is the NET amount when tax is withheld at source and the gross
   * otherwise — the same figure `getCollectibleTotal` derives and the same one
   * `ArPaymentSchedule` rows are booked at, because a withheld invoice is settled at net.
   * A schedule that does not sum to it is unsettleable by construction: the invoice can
   * never reach PAID, and the difference sits in the cash forecast forever as expected
   * cash nobody will send (or hides cash that will arrive).
   *
   * Comparison is EXACT decimal, never rounded and never a float epsilon: a one-centavo
   * drift is a real discrepancy in the integrator's plan and is reported as one. The
   * message names both the submitted sum and the expected total so the caller can see the
   * delta without recomputing it.
   *
   * The generated form cannot raise this — `computeInstallmentSchedule` floors every
   * installment but the last and gives the last the remainder, so the split sums to the
   * total by construction.
   */
  INSTALLMENT_TOTAL_MISMATCH: 'INSTALLMENT_TOTAL_MISMATCH',
  /**
   * `POST /v1/contracts/by-reference/:ref/renew` (ALC-2825) already has an APPROVED RENEWAL
   * amendment for the requested `renewalTermStartDate`, and this request's resulting price
   * (always the contract's CURRENT carried-forward price — this route has no repricing
   * capability) does not match what that amendment actually recorded — e.g. the internal
   * auto-renewal sweep already renewed this same term at a different, index-adjusted price.
   *
   * A term can only be renewed once — replaying the EXACT same request (same
   * `renewalTermStartDate`, same resulting price) is a safe no-op (200, no new amendment,
   * no new Version), but a request for the same term with a DIFFERENT resulting price is
   * refused rather than silently accepted or silently ignored, because either choice would
   * hide which price actually governs that term from the caller.
   */
  CONTRACT_ALREADY_RENEWED: 'CONTRACT_ALREADY_RENEWED',
  /**
   * `renewalTermStartDate` on a renew request does not equal this contract's next term start
   * (`currentTermEndDate + 1 day`), and it does not match any existing RENEWAL amendment's
   * `renewalTermStartDate` either — so it cannot be resolved as either a fresh renewal or a
   * replay of one already recorded (ALC-2825).
   *
   * Also raised when the requested boundary, while equal to `currentTermEndDate + 1 day`,
   * does not align with the contract's own billing-cycle boundary (the internal renewal
   * executor's own alignment check, reached identically here) — an edge case only reachable
   * with a non-default `billingDayOfMonth`.
   *
   * Also raised when a Version already sits AT OR AFTER the requested boundary from an
   * unrelated cause — e.g. an independently approved `/price-change` effective the same
   * date, or further out mid-term — since this route never adopts or reorders an existing
   * Version: `versionNumber` order must match `effectiveFrom` order, so inserting a Version
   * dated at or before a later one already on file would corrupt that invariant.
   */
  CONTRACT_RENEWAL_TERM_MISMATCH: 'CONTRACT_RENEWAL_TERM_MISMATCH',
} as const;
export type ExternalApiErrorCode = (typeof ExternalApiErrorCode)[keyof typeof ExternalApiErrorCode];

/**
 * Flat integrator metadata accepted on External API create/replay bodies and echoed
 * back on responses (ALC-2236, v1 pass-through — no key registry, no schema).
 *
 * Values are strings only: the integrator encodes anything else (numbers, booleans,
 * nested structures) as a string. Bounds are enforced by a service-layer helper
 * (`assertValidMetadata`), not by DTO decorators — see {@link ExternalApiErrorCode}'s
 * `METADATA_INVALID` doc for why.
 */
export type ExternalMetadata = Record<string, string>;

/** Maximum number of keys a `metadata` object may carry (ALC-2236). */
export const EXTERNAL_METADATA_MAX_KEYS = 20;
/** Maximum length of a `metadata` object key (ALC-2236). */
export const EXTERNAL_METADATA_MAX_KEY_LENGTH = 64;
/** Maximum length of a `metadata` object string value (ALC-2236). */
export const EXTERNAL_METADATA_MAX_VALUE_LENGTH = 256;

export interface ExternalApiErrorBody {
  error: {
    code: ExternalApiErrorCode;
    message: string;
    requestId: string;
    retriable?: boolean;
    details?: Record<string, unknown>;
  };
}

/**
 * Non-fatal issue detected while ingesting an External API bill.
 *
 * Document-backed ingestion (multipart create + the async external form) never
 * rejects the whole submission when submitted metadata cannot be applied (e.g.
 * an invalid vendor tax ID). The problem is recorded here and surfaced on the
 * bill/intake response so integrators can react instead of failing silently.
 */
export interface ExternalIngestionWarning {
  code: ExternalApiErrorCode;
  /** Logical field the warning relates to (e.g. `vendor`). */
  field?: string;
  message: string;
}

export interface ExternalCreateBillRequest {
  entityId: string;
  vendorId?: string;
  vendorTaxId?: string;
  /** Required with vendorTaxId when the vendor must be auto-created internally. */
  vendorLegalName?: string;
  amount?: string;
  competencyDate?: string;
  dueDate?: string;
  externalReferenceId: string;
  structuredFields?: ExternalCreateBillStructuredFields;
}

export interface ExternalCreateBillStructuredFields {
  paymentMethod?: string;
  paymentDetails?: string;
  scheduledDate?: string;
  dueDate?: string;
  competencyDate?: string;
  amount?: string;
  vendorTaxId?: string;
  vendorLegalName?: string;
  costCenterText?: string;
  classificationText?: string;
  observations?: string;
  docFiscalEmitido?: boolean;
}

export interface StructuredIntakePaymentDetails {
  rawText?: string;
  barcode?: string;
  boletoNossoNumero?: string;
  pixCopiaECola?: string;
  pixKey?: string;
  bankCode?: string;
  bankName?: string;
  agency?: string;
  agencyDigit?: string;
  accountNumber?: string;
  accountDigit?: string;
  accountHolderName?: string;
}

export interface StructuredIntake {
  sourceChannel: 'EXTERNAL_FORM';
  externalReferenceId: string;
  entityId: string;
  vendorId?: string;
  vendorTaxId?: string;
  vendorLegalName?: string;
  amount?: number;
  scheduledDate?: string;
  dueDate?: string;
  competencyDate?: string;
  paymentMethod?: PaymentMethodType;
  paymentDetails?: StructuredIntakePaymentDetails;
  costCenterText?: string;
  classificationText?: string;
  observations?: string;
  docFiscalEmitido?: boolean;
  /** Snapshot of the submitted External API JSON payload, after idempotency-key resolution. */
  submittedPayload?: ExternalCreateBillRequest;
  /** Non-fatal issues detected while applying the submitted metadata. */
  warnings?: ExternalIngestionWarning[];
}

export interface ExternalBillResponse {
  id: string;
  status: ApBillStatus;
  entityId: string;
  vendorId: string | null;
  amount: string;
  externalReferenceId: string;
  processingStatus: ExternalBillProcessingStatus;
  documentId?: string | null;
  createdAt: string;
  /** Non-fatal issues detected while applying the submitted metadata. */
  warnings?: ExternalIngestionWarning[];
}

export const ExternalFormBillRequiredActionType = {
  DOCUMENT_PASSWORD: 'DOCUMENT_PASSWORD',
} as const;
export type ExternalFormBillRequiredActionType =
  (typeof ExternalFormBillRequiredActionType)[keyof typeof ExternalFormBillRequiredActionType];

export interface ExternalFormBillRequiredAction {
  type: ExternalFormBillRequiredActionType;
  documentId: string;
  originalName: string;
}

export interface ExternalFormBillResponse {
  id: string | null;
  status: ApBillStatus | null;
  entityId: string;
  vendorId: string | null;
  amount: string;
  externalReferenceId: string;
  intakeId: string;
  processingStatus: ExternalBillProcessingStatus;
  documentId?: string | null;
  bundleId?: string | null;
  requiredActions?: ExternalFormBillRequiredAction[];
  createdAt: string;
  /** Non-fatal issues detected while applying the submitted metadata. */
  warnings?: ExternalIngestionWarning[];
}

export interface ExternalEntitySummary {
  id: string;
  name: string;
  tradeName: string | null;
  taxId: string | null;
}

export interface ExternalEntityListResponse {
  entities: ExternalEntitySummary[];
}
